import React, { useState } from 'react'
import { useUser } from 'util/UserContext'
import { Info } from 'lucide-react'
import api from 'config/axiosConfig'
import Modal from 'ui/Modal'
import InputTextArea from 'ui/InputTextArea'

interface Props {
    entityId: number
    entityType: string
    open: boolean
    onClose: () => void
}

const REPORT_REASONS = [
    'Harassment',
    'Misinformation',
    'Threatening violence',
    'Hate speech',
    'Sharing personal information',
    'Impersonation',
    'Self-harm or suicide',
    'Spam',
]

const Report: React.FC<Props> = ({ 
    entityId, 
    entityType, 
    open, 
    onClose 
}) => {
    const { currentUser, setShowAuthModal } = useUser()
    const [selectedReason, setSelectedReason] = useState<string | null>(null)
    const [description, setDescription] = useState('')
    const [error, setError] = useState<string>('')
    const [success, setSuccess] = useState<string>('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleReasonSelect = (reason: string) => {
        setSelectedReason(reason === selectedReason ? null : reason)
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!currentUser) {
            setShowAuthModal(true)
            return
        }

        if (!selectedReason) {
            setError('Please select a reason for reporting.')
            return
        }

        setIsSubmitting(true)
        setError('')
        setSuccess('')

        try {
            await api.post('/api/reports', {
                entityType,
                entityId,
                reason: selectedReason,
                description: description.trim() || null,
            })
            setSuccess('Report submitted successfully!')
            setTimeout(() => {
                handleCloseModal()
            }, 2000)
        } catch (err) {
            setError('Failed to submit report. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleCloseModal = () => {
        onClose()
        setSelectedReason(null)
        setDescription('')
        setError('')
        setSuccess('')
    }

    return (
        <Modal 
            isOpen={open} 
            onClose={handleCloseModal}
            title="Submit a report"
        >
            <form onSubmit={handleSubmit} className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto">
                <div className="flex flex-col flex-grow justify-between mb-4 space-y-4">
                    <div className="flex flex-col flex-grow space-y-4">
                        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            Thanks for looking out for the communitea by reporting things that break the rules. 
                            Let us know what's going on, and we'll look into it.
                        </p>
                        
                        <div className="form-control">
                            <div className="flex flex-wrap gap-2">
                                {REPORT_REASONS.map((reason) => (
                                    <button
                                        key={reason}
                                        type="button"
                                        onClick={() => handleReasonSelect(reason)}
                                        className={`px-3 py-1.5 rounded-full text-sm transition-colors
                                            ${selectedReason === reason 
                                                ? 'btn-primary' 
                                                : 'btn-secondary'
                                            }`}
                                    >
                                        {reason}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="form-control">
                            <InputTextArea
                                id="description"
                                name="description"
                                labelText="Tell us more"
                                value={description}
                                onChange={(e) => setDescription(e.target.value.slice(0, 500))}
                                maxLength={500}
                                autoComplete="off"
                            />
                        </div>

                        {(error || success) && (
                            <div className="flex items-center space-x-2">
                                {error && (
                                    <div className="flex items-center text-red-500 text-sm">
                                        <Info className="mr-1" />
                                        <span>{error}</span>
                                    </div>
                                )}
                                {success && (
                                    <div className="flex items-center text-green-600 text-sm">
                                        <Info className="mr-1" />
                                        <span>{success}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex flex-grow flex-col pb-4 justify-end place-items-end mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
                        <button
                            type="button"
                            onClick={handleCloseModal}
                            className="btn-secondary w-full sm:w-fit py-2 px-4"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting || !selectedReason || success === 'Report submitted successfully!'}
                            className={`w-full sm:w-fit py-2 px-4 ${
                                isSubmitting || !selectedReason || success === 'Report submitted successfully!'
                                    ? 'btn-primary-disabled'
                                    : 'btn-primary'
                            } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                    </div>
                </div>
            </form>
        </Modal>
    )
}

export default Report
